import { takeLatest, call, put, all, fork } from 'redux-saga/effects';
import {
  cognitoCompleteNewPasswordChallenge,
} from '@services/cognitoUserService';
import { confirmForgotPassword, postPasswordChangedAtWithToken } from '@services/authenticationService';
import { LOCAL_STORAGE_ITEM, removeFromLocalStorage } from '@utils/localStorageHelper';
import { setRedirectUrl } from '@containers/App/components/Redirector/actions';
import { ROUTES } from '@shared/constants';
import {
  Types,
  onPasswordResetFail,
  onPasswordResetSuccess,
} from './actions';

const toSerializableError = (err) => {
  if (!err) return { message: 'Request failed' };
  if (typeof err === 'string') return { message: err };

  const code = err.code || err.name || err.__type;
  const message = err.message || String(err);

  return {
    code,
    name: err.name,
    __type: err.__type,
    message,
  };
};

function* callPostResetPasswordData(action) {
  try {
    const {
      password,
      cognitoChallenge,
      cognitoForgotPassword,
    } = action.payload;

    // Cognito "NEW_PASSWORD_REQUIRED" challenge flow
    if (cognitoChallenge) {
      const {
        email,
        session,
        userAttributes,
        requiredAttributes,
      } = cognitoChallenge;
      const result = yield call(cognitoCompleteNewPasswordChallenge, {
        email,
        session,
        newPassword: password,
        userAttributes,
        requiredAttributes,
      });

      if (!result?.success && result?.challenge) {
        const challengeParameters = result?.payload?.challengeParameters || {};
        const challengeSession = result?.payload?.session || null;
        const challengeEmail = result?.payload?.email || email;

        removeFromLocalStorage(LOCAL_STORAGE_ITEM.COGNITO_NEW_PASSWORD_CHALLENGE);
        yield put(setRedirectUrl(ROUTES.MFA_VERIFICATION, {
          session: challengeSession,
          username: challengeEmail,
          challengeName: result.challenge,
          deliveryMedium: challengeParameters?.CODE_DELIVERY_DELIVERY_MEDIUM,
          destination: challengeParameters?.CODE_DELIVERY_DESTINATION,
          shouldSyncPasswordChangedAt: true,
        }));
        return;
      }

      // sync admin-managed custom:pwdChangedAt using the fresh access token.
      // Do not fail the reset flow if this call fails.
      try {
        const token = result?.payload?.token || null;
        if (token) {
          yield call(postPasswordChangedAtWithToken, token);
        }
      } catch (_) {
        // ignore
      }

      removeFromLocalStorage(LOCAL_STORAGE_ITEM.COGNITO_NEW_PASSWORD_CHALLENGE);
      yield put(onPasswordResetSuccess());
      return;
    }

    // Cognito forgot-password flow (verification code + new password)
    if (cognitoForgotPassword) {
      const { email, code } = cognitoForgotPassword;
      const confirmResult = yield call(confirmForgotPassword, {
        email,
        code,
        newPassword: password,
      });
      if (!confirmResult?.success) {
        throw confirmResult?.error || new Error('Password reset failed');
      }
      yield put(onPasswordResetSuccess());
      return;
    }

    throw new Error('Password reset flow is not supported');
  } catch (error) {
    yield put(onPasswordResetFail(toSerializableError(error)));
  }
}

function* watchCallPostResetPasswordData() {
  yield takeLatest(Types.PASSWORD_RESET, callPostResetPasswordData);
}

export default function* watchLoginResetPasswordActions() {
  yield all([fork(watchCallPostResetPasswordData)]);
}
